import { useState, useEffect } from "react";
import { Search, Cpu, Globe, ChevronRight } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const ORIENT_COLORS = { care: "#34d399", utility: "#60a5fa", creative: "#a78bfa", extraction: "#f87171" };
const FORM_COLORS = { wearable: "#34d399", robotics: "#60a5fa", hardware: "#a78bfa", embedded: "#f59e0b", hybrid: "#06b6d4" };

export const HardwareExplorer = () => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterOrient, setFilterOrient] = useState("");
  const [filterForm, setFilterForm] = useState("");
  const [visibleCount, setVisibleCount] = useState(48);

  useEffect(() => {
    axios.get(`${API}/galaxy/devices`, { params: { limit: 2500 } })
      .then(r => { setDevices(r.data); setLoading(false); })
      .catch(() => {
        // Fallback: get from anatomy
        axios.get(`${API}/anatomy/devices/overview`).then(r => {
          setDevices(r.data.notable_devices || []);
          setLoading(false);
        }).catch(() => setLoading(false));
      });
  }, []);

  const filtered = devices.filter(d => {
    if (search && !d.product_name?.toLowerCase().includes(search.toLowerCase()) && !d.tagline?.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterOrient && d.value_orientation !== filterOrient) return false;
    if (filterForm && d.form_factor !== filterForm) return false;
    return true;
  });

  const visible = filtered.slice(0, visibleCount);

  return (
    <div className="space-y-5 animate-fade-in" data-testid="hardware-explorer">
      <div>
        <h2 className="text-xl font-bold mb-1">Device Explorer</h2>
        <p className="text-sm text-muted-foreground">Browse {devices.length.toLocaleString()} physical AI devices</p>
      </div>

      {/* Search + Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search devices..."
            className="w-full pl-9 pr-3 py-2 bg-card border border-border rounded-lg text-sm focus:outline-none focus:border-blue-500/50" />
        </div>
        <select value={filterOrient} onChange={e => setFilterOrient(e.target.value)}
          className="px-3 py-2 bg-card border border-border rounded-lg text-sm">
          <option value="">All orientations</option>
          <option value="care">Care</option>
          <option value="utility">Utility</option>
          <option value="creative">Creative</option>
          <option value="extraction">Extraction</option>
        </select>
        <select value={filterForm} onChange={e => setFilterForm(e.target.value)}
          className="px-3 py-2 bg-card border border-border rounded-lg text-sm">
          <option value="">All form factors</option>
          <option value="wearable">Wearable</option>
          <option value="robotics">Robotics</option>
          <option value="hardware">Hardware</option>
          <option value="embedded">Embedded</option>
        </select>
      </div>

      <p className="text-xs text-muted-foreground">Showing {visible.length} of {filtered.length} devices</p>

      {/* Device grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {visible.map((d, i) => (
          <div key={i} className="bg-card border border-border rounded-xl p-4 hover:border-blue-500/30 transition-colors">
            <div className="flex items-start gap-2 mb-2">
              <span className="w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0" style={{ background: ORIENT_COLORS[d.value_orientation] || "#64748b" }} />
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold truncate">{d.product_name}</h4>
                <p className="text-xs text-muted-foreground line-clamp-2">{d.tagline || d.description}</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] px-2 py-0.5 rounded-full border" style={{ color: ORIENT_COLORS[d.value_orientation], borderColor: `${ORIENT_COLORS[d.value_orientation]}33` }}>
                {d.value_orientation}
              </span>
              <span className="text-[10px] px-2 py-0.5 rounded-full border" style={{ color: FORM_COLORS[d.form_factor] || "#64748b", borderColor: `${FORM_COLORS[d.form_factor] || "#64748b"}33` }}>
                {d.form_factor}
              </span>
              {d.country && <span className="text-[10px] text-muted-foreground flex items-center gap-0.5"><Globe className="w-2.5 h-2.5" />{d.country}</span>}
            </div>
          </div>
        ))}
      </div>

      {visibleCount < filtered.length && (
        <div className="text-center">
          <button onClick={() => setVisibleCount(v => v + 48)}
            className="px-6 py-2 bg-blue-500/10 border border-blue-500/20 rounded-lg text-sm text-blue-400 hover:bg-blue-500/20 transition-colors">
            Load more ({(filtered.length - visibleCount).toLocaleString()} remaining)
          </button>
        </div>
      )}
    </div>
  );
};

export default HardwareExplorer;
